import { useState } from "react";
import { offersApi, ApiError } from "../utils/api";
import { toCamelCase } from "../utils/fieldMapper";
import { formatTimeAgo } from "../utils/formatters";
import { playBell } from "../utils/sounds";

export default function useOffers({ isAuthenticated, isDev, notify, loginWithRedirect, getAccessTokenSilently }) {
  const [jobOffers, setJobOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");
  const [offerMessage, setOfferMessage] = useState("");
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [offerTransport, setOfferTransport] = useState("has_car");
  const [offerPickupAddress, setOfferPickupAddress] = useState("");
  const [offerPickupLat, setOfferPickupLat] = useState(null);
  const [offerPickupLng, setOfferPickupLng] = useState(null);
  const [acceptedOffer, setAcceptedOffer] = useState(null);

  const fetchOffers = async (jobId) => {
    if (!isAuthenticated && !isDev) { setJobOffers([]); return; }
    setOffersLoading(true);
    try {
      const result = await offersApi.list(jobId, getAccessTokenSilently);
      setJobOffers((result.data || []).map(o => {
        const oo = toCamelCase(o);
        return { ...oo, userName: oo.userName || 'Notandi', userAvatar: oo.userAvatar || '👤',
          amount: oo.price, status: oo.status || 'pending', time: formatTimeAgo(oo.createdAt),
          transportType: oo.transportType || 'has_car' };
      }));
    } catch (err) { console.error('Failed to fetch offers:', err); setJobOffers([]); }
    finally { setOffersLoading(false); }
  };

  const fetchAcceptedOffer = async (jobId) => {
    if (!isAuthenticated && !isDev) return;
    try {
      const result = await offersApi.getAccepted(jobId, getAccessTokenSilently);
      setAcceptedOffer(result.data ? {
        transportType: result.data.transport_type,
        pickupAddress: result.data.pickup_address,
        pickupLat: result.data.pickup_lat,
        pickupLng: result.data.pickup_lng,
        userName: result.data.user_name,
      } : null);
    } catch (err) { console.error('Failed to fetch accepted offer:', err); }
  };

  const handleSubmitOffer = async (jobId, amount, message, transport) => {
    if (!amount) return;
    if (!isAuthenticated && !isDev) { notify("⚠️ Þú þarft að skrá þig inn"); loginWithRedirect(); return; }
    setOfferSubmitting(true);
    try {
      const offerData = {
        job_id: jobId, price: amount, message,
        transport_type: transport?.type || offerTransport,
        pickup_address: transport?.address || offerPickupAddress || undefined,
        pickup_lat: transport?.lat ?? offerPickupLat ?? undefined,
        pickup_lng: transport?.lng ?? offerPickupLng ?? undefined,
        needs_help_carrying: transport?.needsHelpCarrying ? 1 : 0,
        has_dolly: transport?.hasDolly ? 1 : 0,
        has_stair_climber: transport?.hasStairClimber ? 1 : 0,
        disclaimer_accepted: transport?.disclaimerAccepted ? 1 : 0,
      };
      await offersApi.create(offerData, getAccessTokenSilently);
      resetOfferForm();
      notify("✅ Tilboð sent!"); playBell(0.25); fetchOffers(jobId);
    } catch (error) {
      console.error('Failed to create offer:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að senda tilboð");
    } finally { setOfferSubmitting(false); }
  };

  const handleAcceptOffer = async (offerId, jobId) => {
    try { await offersApi.updateStatus(offerId, 'accepted', getAccessTokenSilently); playBell(); notify("✅ Tilboð samþykkt!"); fetchOffers(jobId); }
    catch (error) { notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Villa"); }
  };

  const handleRejectOffer = async (offerId, jobId) => {
    try { await offersApi.updateStatus(offerId, 'rejected', getAccessTokenSilently); notify("Tilboði hafnað"); fetchOffers(jobId); }
    catch (error) { notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Villa"); }
  };

  const resetOfferForm = () => {
    setShowOfferForm(false); setOfferAmount(""); setOfferMessage("");
    setOfferTransport("has_car"); setOfferPickupAddress(""); setOfferPickupLat(null); setOfferPickupLng(null);
  };

  return {
    jobOffers, offersLoading, showOfferForm, setShowOfferForm,
    offerAmount, setOfferAmount, offerMessage, setOfferMessage,
    offerSubmitting, offerTransport, setOfferTransport,
    offerPickupAddress, setOfferPickupAddress,
    offerPickupLat, setOfferPickupLat, offerPickupLng, setOfferPickupLng,
    acceptedOffer,
    fetchOffers, fetchAcceptedOffer,
    handleSubmitOffer, handleAcceptOffer, handleRejectOffer,
    resetOfferForm,
  };
}
